import { classNames } from '@chiroup/core/functions/classNames';
import {
  HealthcomDiscipline,
  HealthcomTreatment,
} from '@chiroup/core/types/HealthComLocation.type';
import { RadioGroup } from '@headlessui/react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import React from 'react';

type Props = {
  disciplines: HealthcomDiscipline[];
  onChange: (treatment: number) => void;
  value: number | null;
};

const ScheduleTreatments: React.FC<Props> = ({
  disciplines,
  onChange,
  value,
}) => {
  const treatments = disciplines
    ?.reduce(
      (arr: HealthcomTreatment[], discipline) => [
        ...arr,
        ...discipline.treatments,
      ],
      [],
    )
    ?.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <RadioGroup value={value} onChange={onChange}>
      <RadioGroup.Label>
        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-900 whitespace-nowrap">
            Select a treatment
          </span>
          <div className="w-full border-t border-gray-300 flex-grow ml-2" />
        </div>
      </RadioGroup.Label>
      <div className="mt-4 grid grid-cols-1 gap-y-2 sm:gap-y-6 sm:grid-cols-2 sm:gap-x-4">
        {treatments.map((treatment) => (
          <RadioGroup.Option
            key={treatment.ID}
            value={treatment.ID}
            className={({ checked, active }) =>
              classNames(
                checked ? 'border-transparent' : 'border-gray-300',
                active ? 'border-primary ring-2 ring-primary' : '',
                'relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none',
              )
            }
          >
            {({ checked, active }) => (
              <>
                <span className="flex flex-1">
                  <span className="flex flex-col">
                    <RadioGroup.Label
                      as="span"
                      className="block text-sm font-medium text-gray-900"
                    >
                      {treatment.name}{' '}
                      {treatment.onlineBookingDuration &&
                      typeof treatment.treatmentDuration === 'number'
                        ? `(${treatment.treatmentDuration} min)`
                        : ''}
                    </RadioGroup.Label>
                    <RadioGroup.Description
                      as="span"
                      className="mt-1 flex items-center text-sm text-gray-500"
                    >
                      {treatment.description}
                    </RadioGroup.Description>
                    {treatment.onlineBookingPrice && (
                      <RadioGroup.Description
                        as="span"
                        className="mt-6 text-sm font-medium text-gray-900"
                      >
                        {treatment.price}
                      </RadioGroup.Description>
                    )}
                  </span>
                </span>
                <CheckCircleIcon
                  className={classNames(
                    !checked ? 'invisible' : '',
                    'h-5 w-5 text-primary',
                  )}
                  aria-hidden="true"
                />
                <span
                  className={classNames(
                    active ? 'border' : 'border-2',
                    checked ? 'border-primary' : 'border-transparent',
                    'pointer-events-none absolute -inset-px rounded-lg',
                  )}
                  aria-hidden="true"
                />
              </>
            )}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  );
};

export default ScheduleTreatments;
